import { useState, useEffect, useRef } from 'react';
import {
  useModalTableMultiselectStates,
  ModalTableMultiselectStates,
} from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { PAGINATION_PARAMS } from '@lib/enums/urls';
import { Values } from '@lib/interfaces/form';

interface UseModalTableMultiunselectProps {
  isOpen: boolean;
  selected?: string[];
  filterStateDefault: Values;
}

interface UseModalTableMultiunselectReturn {
  searchStateSelected: ModalTableMultiselectStates['searchStateSelected'];
  paginationStateSelected: ModalTableMultiselectStates['paginationStateSelected'];
  orderingStateSelected: ModalTableMultiselectStates['orderingStateSelected'];
  filterStateSelected: ModalTableMultiselectStates['filterStateSelected'];
  selectedIdsForQuery: string[];
  setSelectedIdsForQuery: (ids: string[]) => void;
  idsForSelectedTabQuery: string[];
}

export default function useModalTableMultiunselect({
  isOpen,
  selected,
  filterStateDefault,
}: UseModalTableMultiunselectProps): UseModalTableMultiunselectReturn {
  const states = useModalTableMultiselectStates({ filterStateDefault });
  const {
    paginationStateSelected,
    searchStateSelected,
    orderingStateSelected,
    filterStateSelected,
  } = states;
  const [, setPaginationSelected] = paginationStateSelected;
  const [, onSearchHandlerSelected] = searchStateSelected;
  const [filterValuesSelected, setFilterValuesSelected] = filterStateSelected;

  const [selectedIdsForQuery, setSelectedIdsForQuery] = useState<string[]>(
    selected || [],
  );
  const [idsForSelectedTabQuery, setIdsForSelectedTabQuery] = useState<
    string[]
  >([]);
  const prevIsOpenRef = useRef(isOpen);

  // Sync selectedIdsForQuery with selected prop when modal opens or selected changes
  useEffect(() => {
    if (isOpen && selected) {
      // Create new array reference to ensure effects downstream are triggered
      setSelectedIdsForQuery([...selected]);
    }
  }, [isOpen, selected]);

  useEffect(() => {
    if (isOpen) {
      setFilterValuesSelected({ ...(filterStateDefault || {}) });
    }
  }, [isOpen, filterStateDefault, setFilterValuesSelected]);

  useEffect(() => {
    if (isOpen) {
      setIdsForSelectedTabQuery((prev) => [...prev]);
    }
  }, [isOpen, filterValuesSelected]);

  // In SelectedOnly mode (multiunselect), always keep the snapshot in sync
  useEffect(() => {
    if (isOpen) {
      setIdsForSelectedTabQuery(selectedIdsForQuery);
    }
  }, [isOpen, selectedIdsForQuery]);

  // Reset states only when this hook's modal transitions from open -> closed
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      setPaginationSelected(PAGINATION_PARAMS);
      onSearchHandlerSelected('');
      setIdsForSelectedTabQuery([]);
      setFilterValuesSelected({});
    }
    prevIsOpenRef.current = isOpen;
  }, [
    isOpen,
    setPaginationSelected,
    onSearchHandlerSelected,
    setFilterValuesSelected,
  ]);

  return {
    searchStateSelected,
    paginationStateSelected,
    orderingStateSelected,
    filterStateSelected,
    selectedIdsForQuery,
    setSelectedIdsForQuery,
    idsForSelectedTabQuery,
  };
}
